import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams } from "react-router";
import { isEmail, validateUrl } from "../../../libs/RegexUtils";
import SnackbarUtils from "../../../libs/SnackbarUtils";
import historyUtils from "../../../libs/history.utils";
import {
  serviceCreateHealthTips,
  serviceUpdateHealthTips,
  serviceHealthTipsDetail,
} from "../../../services/HealthTips.service";
import { serviceGetList } from "../../../services/index.services";
import defaultImg from "../../../assets/CRMAssets/default_quiz_health_tip.jpg";
import { dataURLtoFile } from "../../../helper/Helper";

const initialForm = {
  title: "",
  link: "",
  description: "",
  short_brief: "",
  program_ids: [],
  image: null,
  start_date: "",
  client_id: "",
  update_reason: "",
  status: true,
};

const useHealthTipsHook = ({ location }) => {
  const [form, setForm] = useState(JSON.parse(JSON.stringify(initialForm)));
  const [errorData, setErrorData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [programList, setProgramList] = useState([]);
  const [listData, setListData] = useState({
    CLIENTS: [],
    PROGRAMS: [],
  });
  const descriptionRef = useRef(null);
  const [image, setImage] = useState("");
  const { id } = useParams();

  const Client = location?.state?.Client || "";
  const customerID = location?.state?.customerID || "";
  const Name = location?.state?.Name || [];
  const isDuplicate = location?.state?.is_duplicate || false;

  useEffect(() => {
    if (!id && Client && Name) {
      setForm({
        ...form,
        client_id: Client,
        program_ids: Array.isArray(Name) ? Name : [Name],
        program_id: customerID,
      });
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      serviceHealthTipsDetail({ id }).then((res) => {
        if (!res?.error) {
          const data = res?.data?.details;
          console.log("Fetched details:", data);
          const obj = {};
          Object.keys({ ...initialForm })?.forEach((key) => {
            if (key === "client_id") {
              obj[key] = {
                label: data?.client?.name,
                name: data.client.name,
                id: data?.client?.id,
              };
            } else if (key === "program_ids") {
              obj[key] = data?.programs?.map((dT) => ({
                ...dT,
                label: dT?.name,
              }));
            } else if (key === "status") {
              obj[key] = data[key] === "ACTIVE";
            } else {
              obj[key] = data[key] || "";
            }
          });

          obj.id = data.id;
          const isDuplicateData = isDuplicate
            ? { program_ids: [], start_date: "", client_id: "",  remote_path: data?.image || "", }
            : {};
          setForm({
            ...obj,
            image: null,
            image_url: data?.image,
            ...isDuplicateData,
          });
          setImage(data?.image);
          setCustomerDetails(data);
        }
      });
    }
  }, [id]);

  useEffect(() => {
    (async () => {
      const promises = await Promise.allSettled([serviceGetList(["CLIENTS"])]);
      const ProductList = promises[0]?.value?.data;
      setListData(ProductList);
    })();
  }, []);

  // const getProgramList = useCallback(
  //   (clientId) => {
  //     (async () => {
  //       if (clientId?.id) {
  //         const promises = await Promise.allSettled([
  //           serviceGetList(["PROGRAMS"], { client_id: clientId?.id }),
  //         ]);
  //         const ProductList = promises[0]?.value?.data?.PROGRAMS;
  //         setProgramList([...ProductList]);
  //       }
  //     })();
  //   },
  //   [programList]
  // );

  const getProgramList = useCallback(() => {
    if (form?.client_id) {
      const req = serviceGetList(["PROGRAMS"], {
        client_id: form?.client_id?.id,
      });
      req.then((res) => {
        if (!res?.error) {
          const resData = res?.data?.PROGRAMS;
          setProgramList([...resData]);
        }
      });
    }
  }, [form?.client_id]);
  
  useEffect(() => {
    if (form?.client_id) {
      getProgramList();
    }
  }, [form?.client_id]);
  console.log("Health form", form);

  const checkFormValidation = useCallback(() => {
    const errors = { ...errorData };
    const fieldLabels = {
      title: "Title",
      description: "Description",
      short_brief: "Short Brief",
      start_date: "Date & Time of Pushing on App",
      client_id: "Client",
      program_ids: "Program",
    };

    let required = [
      "title",
      "description",
      "short_brief",
      "start_date",
      "client_id",
      "program_ids",
    ];
    // if (!id) {
    //   required.push("image");
    // }
    if (!form?.description) {
      SnackbarUtils.error("Please Enter Description");
    }
    if (form?.link && !form.link.startsWith("https://")) {
      errors.link = true;
      errors.link = "Enter URL with https://";
    }
    let missingFields = [];

    required.forEach((val) => {
      if (
        (!form?.[val] && parseInt(form?.[val]) !== 0) ||
        (Array.isArray(form?.[val]) && form?.[val]?.length === 0)
      ) {
        errors[val] = true;
        missingFields.push(val);
      }
    });

    if (missingFields.length > 1) {
      SnackbarUtils.error("Please provide the required fields");
    } else if (missingFields.length === 1) {
      const fieldName =
        missingFields[0] === "image"
          ? "Please Upload Image"
          : `Please Enter ${
              fieldLabels[missingFields[0]] ||
              missingFields[0].replace("_", " ")
            }`;
      SnackbarUtils.error(fieldName);
    }

    Object.keys(errors).forEach((key) => {
      if (!errors[key]) {
        delete errors[key];
      }
    });
    return errors;
  }, [form, errorData]);

  const removeError = useCallback(
    (title) => {
      const temp = JSON.parse(JSON.stringify(errorData));
      temp[title] = false;
      setErrorData(temp);
    },
    [setErrorData, errorData]
  );

  const changeTextData = useCallback(
    (text, fieldName) => {
      let shouldRemoveError = true;
      const t = { ...form };

      if (fieldName === "name") {
        if (text.length <= 80) {
          t[fieldName] = text;
        }
      } else if (fieldName === "start_date") {
        t[fieldName] = new Date(text);
      } else if (fieldName === "client_id") {
        t[fieldName] = text;
        t["program_ids"] = [];
        getProgramList(text);
      } else if (fieldName === "program_ids") {
        const uniqueData = Object.values(
          text?.reduce((acc, item) => {
            acc[item?.id] = item;
            return acc;
          }, {})
        );
        t[fieldName] = uniqueData;
      } else {
        t[fieldName] = text;
      }
      setForm(t);
      shouldRemoveError && removeError(fieldName);
    },
    [removeError, form, setForm]
  );
  descriptionRef.current = changeTextData;

  const submitToServer = useCallback(() => {
    if (!isSubmitting) {
      setIsSubmitting(true);

      const formData = new FormData();
      Object.keys({ ...initialForm }).forEach((key) => {
        if (key === "client_id") {
          formData.append("client_id", form?.client_id?.id);
        } else if (key === "program_ids") {
          const getID = form?.program_ids?.map((val) => val?.id);
          formData.append("program_ids", JSON.stringify(getID));
        } else if (key === "status") {
          formData.append("is_active", form[key] ? true : false);
        } else if (key === "image") {
          if (form?.image) {
            formData.append(key, form?.image);
          } else {
          
            formData.append(key, form[key]);
           
          }
        } else {
          formData.append(key, form[key]);
        }
      });
      if (form?.image_url) {
        formData.append("image", form?.image_url);
      }
      // if (isDuplicate && form?.remote_path) {
      //   formData.append("image", form?.image_url);
      // }
      if (form?.id && !isDuplicate) {
        formData.append("id", form?.id);
      }

      const serviceCall =
        form.id && !isDuplicate
          ? serviceUpdateHealthTips
          : serviceCreateHealthTips;
      serviceCall(formData).then((res) => {
        if (!res.error) {
          const message = form.id && !isDuplicate
            ? "Updated successfully"
            : "Created successfully";
          SnackbarUtils.success(message);
          historyUtils.goBack();
        } else {
          SnackbarUtils.error(res?.message);
        }
        setIsSubmitting(false);
      });
    }
  }, [form, isSubmitting, setIsSubmitting]);

  const onBlurHandler = useCallback(
    (type) => {
      if (form?.[type]) {
        changeTextData(form?.[type].trim(), type);
      }
    },
    [changeTextData]
  );

  const handleSubmit = useCallback(
    async (status) => {
      const errors = checkFormValidation();
      if (Object.keys(errors)?.length > 0) {
        setErrorData(errors);
        return true;
      }
      submitToServer(status);
    },
    [checkFormValidation, setErrorData, form]
  );

  return {
    form,
    errorData,
    changeTextData,
    handleSubmit,
    listData,
    id,
    image,
    onBlurHandler,
    descriptionRef,
    isSubmitting,
    programList,
    defaultImg,
    isDuplicate,
    customerDetails
  };
};

export default useHealthTipsHook;
